import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Box, Button, Divider, Grid, Icon, IconButton, Modal, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FormHandles } from "@unform/core";
import { format } from "date-fns";
import { useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { VForm, VTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { SinisterService } from "../../../../shared/services/api/sinister/SinisterService";

interface ModalRefusedProps {
	open: boolean;
	rowSelect: any;
	onClose: () => void;
	onSuccess: () => void;
}

export const ModalRefused = ({ open, rowSelect, onClose, onSuccess }: ModalRefusedProps) => {
	const formRef = useRef<FormHandles>(null);
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);

	const handleSubmit = async (data: any) => {
		try {
			const schema = yup.object().shape({
				reasonRefused: yup.string().required("Informe o motivo da Recusa"),
			});

			await schema.validate(data, { abortEarly: false });

			const dadosForm = {
				reasonRefused: data.reasonRefused,
			};

			await SinisterService.updateValueRefused(rowSelect?.id, dadosForm);

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title={"Sucesso"} content={"Registro salvo com sucesso"} />,
			});

			onClose();
			onSuccess();
		} catch (err: any) {
			if (err instanceof yup.ValidationError) {
				const validationErrors: { [key: string]: string } = {};
				err.inner.forEach((error) => {
					if (error.path) validationErrors[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErrors);
			} else {
				toastHandler.present({
					type: "error",
					messageOrContent: err.message,
					position: "top-center",
				});
			}
		}
	};

	const InfoItem = ({ icon, label, value, isrefused }: { icon: string; label: string; value: string; isrefused?: boolean }) => (
		<Grid item xs={12} md={4}>
			<Stack direction="row" spacing={2} alignItems="flex-start">
				<Avatar sx={{ bgcolor: "#f0f0f0", width: 32, height: 32, mt: 0.5 }}>
					<Icon fontSize="small" sx={{ color: "primary.main" }}>
						{icon}
					</Icon>
				</Avatar>
				<Box>
					<Stack spacing={0.5}>
						<Typography variant="caption" color="text.secondary">
							{label}
						</Typography>
						<Typography
							variant="subtitle2"
							sx={
								isrefused
									? {
											color: "error.main",
											backgroundColor: "#ffe1d3",
											borderRadius: 2,
											px: 2,
											py: 0.3,
											display: "inline-block",
											width: "fit-content",
									  }
									: {}
							}
						>
							{value}
						</Typography>
					</Stack>
				</Box>
			</Stack>
		</Grid>
	);

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					width: "95%",
					maxWidth: 720,
					maxHeight: "90vh",
					overflowY: "auto",
					borderRadius: 3,
				}}
			>
				<LoadingComp isopen={isLoading} />

				<Grid container justifyContent="space-between" alignItems="center">
					<Typography variant="h4" sx={{ color: "primary.main", fontWeight: 300 }}>
						Informações da Recusa
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Grid>

				<Divider sx={{ my: 2 }} />

				<VForm ref={formRef} onSubmit={handleSubmit} onPointerEnterCapture={""} onPointerLeaveCapture={()=>{}} placeholder={""}>
					<Grid container direction="column" spacing={3} padding={3}>
						<Grid container spacing={2}>
							<InfoItem icon="check_circle" label="Status" value="RECUSADO" isrefused />
							<InfoItem icon="calendar_month" label="Data de Recusa" value={format(new Date(), "dd/MM/yyyy")} />
							<InfoItem
								icon="attach_money"
								label="Valor Original"
								value={parseFloat(`${rowSelect?.value ?? 0}`).toLocaleString("pt-br", {
									style: "currency",
									currency: "BRL",
								})}
							/>
						</Grid>

						<Divider sx={{ my: 2 }} />

						<Grid container spacing={2}>
							<Grid item xs={12} md={12}>
								<VTextField fullWidth label="Motivo da Recusa" name="reasonRefused" disabled={false} multiline rows={4} />
							</Grid>
						</Grid>

						<Grid container justifyContent="flex-end" flexDirection={smDown ? "column" : "row"} gap={2} mt={3}>
							<Button color="inherit" variant="contained" startIcon={<Icon>arrow_back</Icon>} onClick={onClose} sx={{ minWidth: 140 }}>
								Voltar
							</Button>
							<Button
								color="primary"
								variant="contained"
								startIcon={<Icon>check</Icon>}
								onClick={() => formRef.current?.submitForm()}
								sx={{ minWidth: 140 }}
							>
								Confirmar
							</Button>
						</Grid>
					</Grid>
				</VForm>
			</Box>
		</Modal>
	);
};
